/**
 * The plan builder — one phase's pattern, and the dock that programmes a day out of it.
 *
 * Composition only. `usePlanBuilder` owns the reads and the wiring, `model/weekViewModel.ts` owns
 * every label and every number, and the entities own every rule. This file decides which of six
 * states to show and nothing else.
 *
 * ── The top bar renders in EVERY state, and that is deliberate ───────────────────────────────
 * `AppShell` moves focus to the new screen's `h1` after a route change, which is what announces
 * the page to a screen-reader user and what makes the next Tab continue from the right place. A
 * screen that renders no heading until its data arrives is a screen the shell silently fails to
 * announce — and the shell's own comment records that the house rule is "a screen renders its `h1`
 * in every state, skeletons included".
 *
 * The heading is the PLAN's name, so it also has to be right before the tree lands. Until then it
 * falls back to the plan's own id: two cold tabs stay distinguishable and nothing is fabricated.
 * That is the trick `usePlanName` used to do out of the library's cache, and it is not needed here
 * — the builder loads the plan itself, so the name arrives on the only read there is, warm cache
 * or cold URL alike.
 *
 * ── Assign is reachable from EVERY state, including the ones that render no canvas ────────────
 * The top bar renders in every state, and so does its Assign button: `useAssignGate` judges the
 * STORED plan, not what the canvas can draw.
 *
 * ── A plan built FOR a client is assigned, not saved (23 September 2026) ─────────────────────
 * The client detail's „Nowy plan" seeds an unsaved plan with a recipient. While it has one, the
 * bar offers no Zapisz (and Cmd/Ctrl+S does nothing): the assign dialog sends the whole tree to the
 * client, and its „Zapisz też jako szablon" is how the plan reaches the library too. Once the
 * assignment is written the plan is settled — no longer unsaved work — and closing the dialog takes
 * the trainer back to that client, replacing `/plans/nowy` in history so Back cannot reopen a plan
 * that has already been given away.
 *
 * ── One canvas at a time, and the screen is what enforces it ─────────────────────────────────
 * `WeekCanvas` and `TapeCanvas` each bind a drag MONITOR, and every monitor accepting a source sees
 * that source's drop — so two mounted together would dispatch one gesture twice. `usePlanBuilder`
 * answers with exactly one of `week` and `tape`, and this file mounts the one it got.
 */

import { Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  setPlanName,
  settleAssignedNewPlan,
  settleAssignedPlanDraft,
  useIsForkPlan,
  useIsLibraryEntryPlan,
  useIsUnsavedNewPlan,
  usePlanRecipient,
  usePlanSchedulingMode,
} from '~/entities/plan-draft';
import { useSessionUser } from '~/entities/session';
import { BuddyPanel } from '~/features/ai-chat';
import { AssignPlanDialog, useAssignGate } from '~/features/assign-plan';
import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';

import { useBuilderDayDock } from '../lib/useBuilderDayDock';
import { usePhaseEditing } from '../lib/usePhaseEditing';
import { usePlanAssistant } from '../lib/usePlanAssistant';
import { usePlanBuilderSearch } from '../lib/usePlanBuilderSearch';
import { usePlanBuilder } from '../lib/usePlanBuilder';
import type { PlanBuilderTarget } from '../lib/usePlanDocument';
import { usePlanLifecycle } from '../lib/usePlanLifecycle';
import { usePlanViewLinks } from '../lib/usePlanViewLinks';
import { useSavePlanDocument } from '../lib/useSavePlanDocument';
import { useLiveNote } from '../lib/useLiveNote';
import { usePlanShortcuts } from '../lib/usePlanShortcuts';

import { BuilderNote } from './BuilderNote';
import { BuilderNotice } from './BuilderNotice';
import { NewPlanNotice } from './NewPlanNotice';
import { BuilderRail } from './BuilderRail';
import { BuilderTopBar } from './BuilderTopBar';
import { ConflictDialog } from './ConflictDialog';
import { DayDock } from './DayDock';
import { PatternBar } from './PatternBar';
import { PhaseBar } from './PhaseBar';
import { TapeCanvas } from './TapeCanvas';
import { WeekCanvas } from './WeekCanvas';
import styles from './PlanBuilderScreen.module.css';

export interface PlanBuilderScreenProps {
  /**
   * Which document this mount is about — a stored plan, or the draft the create modal seeded.
   *
   * A PROP rather than a `useParams` read, because two routes mount this screen and only one of them
   * has a parameter. It also means the screen no longer knows the router's route tree, which is what
   * lets `/plans/nowy` exist as a plain sibling route instead of as a magic `$planId` value.
   */
  readonly target: PlanBuilderTarget;
}

export function PlanBuilderScreen({ target }: PlanBuilderScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const panelId = useId();

  const resolvedId = target.kind === 'stored' ? target.planId : null;

  /* The plan's own irreversible acts — deleting this programme, or ending the client's plan that
   * was cut from it. Owner, 17 September 2026; the menu's whole rule is `model/planLifecycleMenu`.
   * `announce` goes to the phase region below, which is the screen's one live region for an act
   * whose outcome the canvas does not itself show. */
  const [lifecycleNotice, setLifecycleNotice] = useState('');
  const lifecycle = usePlanLifecycle({
    planId: resolvedId,
    isLibraryEntry: useIsLibraryEntryPlan(),
    isSelfPaced: usePlanSchedulingMode() === 'selfpaced',
    announce: setLifecycleNotice,
  });
  const builder = usePlanBuilder(target);
  const save = useSavePlanDocument(resolvedId);
  const dock = useBuilderDayDock();
  const assign = useAssignGate(resolvedId);
  /* The Buddy panel beside the week (board S): opened from „Asystent", kept in the address. It stands
   * in the row and narrows the week; the day panel may stand beside it (S2), and then the row keeps
   * the day panel's width clear on its right, so the two never overlap. */
  const search = usePlanBuilderSearch();
  const userId = useSessionUser()?.id ?? null;
  const assistantShown = search.assistantOpen && builder.status === 'ready';
  const assistant = usePlanAssistant(userId ?? '', assistantShown && userId !== null);
  const assistantOpen = assistantShown && assistant !== null;
  // „Asystent", for the panel's close to hand focus back to: the ✕ goes with the panel.
  const assistantButton = useRef<HTMLButtonElement>(null);
  const [assigning, setAssigning] = useState(false);
  // The client an unsaved plan is being built for, and — once the assignment is written — the one
  // who has it: the store keeps the recipient through the settle. A recipient's plan that is no
  // longer unsaved work is exactly an assigned one, and closing its confirmation returns to them.
  const recipient = usePlanRecipient();
  const unsavedWork = useIsUnsavedNewPlan();
  const assignedTo = recipient !== null && !unsavedWork ? recipient.clientId : null;
  // One ref, two consumers, both here: the bar attaches it to the Assign button and the dialog
  // hands focus back to it on close. Base UI cannot find the button on its own — it is not a
  // `Dialog.Trigger`, because the top bar owns the layout.
  const assignButton = useRef<HTMLButtonElement>(null);
  // The screen's own box. Read by the dismissal handler below, which has to tell a press on the
  // week from a press inside the portalled day panel — see its comment.
  const pageRef = useRef<HTMLElement>(null);

  const editable = builder.renameSession !== null;
  /* The one-time note that says what editing a running plan does to the client's calendar — mobile's
   * `LivePlanNote`, in mobile's words, dismissed once per browser. Only a fork has a client behind
   * it, and `useIsForkPlan` is the same predicate the delete confirm's wording reads. */
  const isFork = useIsForkPlan();
  const liveNote = useLiveNote(isFork);
  // The switch back to the running plan's Przegląd and Kalendarz — see the hook.
  const views = usePlanViewLinks(resolvedId);
  // Cmd/Ctrl+Z, its Shift twin and Cmd/Ctrl+S, all at the document. The save chord is what makes the
  // plan saveable from inside the day panel's focus trap, where the bar's own Zapisz is `aria-hidden`
  // and out of reach; every refusal is the save's own, worded by it.
  usePlanShortcuts({ undo: save.undo, redo: save.redo, save: save.save, enabled: editable });

  // The chrome is IDENTICAL under both canvases — same plan, same phases, same six bars, same lock —
  // so it is read off whichever one is live rather than branched on twice below. The pattern band is
  // read the same way and for the same reason: one component, and the canvas decides what its pill
  // holds.
  const chrome = builder.week?.chrome ?? builder.tape?.chrome ?? null;
  const pattern = builder.week?.pattern ?? builder.tape?.pattern ?? null;
  // The band's writes, and the ONE phase-delete confirm. Bound to the phase the canvas is showing,
  // which is the only phase the band's editor acts on.
  const phase = usePhaseEditing(
    builder.phaseIndex,
    chrome?.phaseEditor ?? null,
    builder.selectPhase,
  );
  const activeTabId = `phase-tab-${String(
    chrome?.phases.find((phase) => phase.isActive)?.index ?? 0,
  )}`;

  /**
   * Create a day and open the dock on it.
   *
   * A day with no exercises is dropped by the serialiser, so creating one and walking away changes
   * nothing on the wire — landing in the form is the only useful next move. `weekday` is `null` on
   * the tape, where a training is appended to the cycle rather than placed on a column.
   */
  const openNewDay = (weekday: number | null) => {
    const token = builder.addDay?.(weekday) ?? null;
    if (token !== null) dock.open(token);
  };

  // A stored plan falls back to the id IN THE ADDRESS so two cold tabs stay distinguishable; a
  // brand-new one has no id to fall back to, and its own words instead.
  //
  // The RAW parameter, not the parsed one: `/plans/abc` parses to null and used to render a bare
  // `#`, which named nothing and titled the tab `# · MoveBuddy`. The address that failed is the one
  // useful thing a heading can say about a plan that will not load.
  const heading =
    builder.planName ??
    (target.kind === 'draft' ? t('web:planBuilder.newPlan') : `#${target.rawId}`);
  useDocumentTitle(heading);

  const backToLibrary = (
    <Link to="/plans" className={styles.back}>
      {t('web:planBuilder.backToLibrary')}
    </Link>
  );

  /* The entrance, minus its rise — see the note on the element itself. */
  const reducedMotion = useScreenMotionPreference();
  const step = screenMotion('enter', { reducedMotion });
  const entrance = {
    initial: { opacity: step.initial.opacity ?? 1 },
    animate: { opacity: step.animate.opacity ?? 1 },
    transition: step.transition,
  };

  return (
    <motion.main
      className={styles.page}
      /* ── Arriving from the library, and the ONE property this may touch ──────────────────────
       * The owner asked for "jakas ladna, ale bardzo bardzo malutka, ledwo zauwazalna" entrance on
       * Programy -> program (9 September 2026), and the app already has one entrance vocabulary:
       * `screenMotion`'s `enter` step, 340ms on the handoff's own curve, which every other screen
       * settles at. Its TRANSITION is taken and its `y` is not — `SummaryColumn` takes the same
       * half for the same reason. A screen loads without moving: 10px of rise on arrival is exactly
       * the jump the policy forbids, and an entrance that fades is the barely-noticeable one the
       * owner asked for anyway. Opacity also costs no layout and creates no containing block, so
       * the day panel's `position: fixed` still resolves against the viewport.
       *
       * On the ROOT rather than on the ready branch: the arrival is the route mounting, so it plays
       * once, for whatever the screen has to show at that moment, and never again when the data
       * lands. Wrapping the ready branch instead would have added a box to a flex column and made
       * the entrance replay every time the plan reloaded. */
      initial={entrance.initial}
      animate={entrance.animate}
      transition={entrance.transition}
      /* ── The week behind the day panel STAYS LIVE ────────────────────────────────────────
       * It was briefly `inert`, on the argument that a subtree Base UI has marked `aria-hidden`
       * must not stay clickable. The reasoning was sound and the conclusion was the wrong one:
       * the owner wants the handoff's behaviour, where a trainer steps between days by pressing
       * the card they want and closes the panel by pressing the week beside it (7 September 2026).
       *
       * So the panel is non-modal instead, which is the honest way to have both — see `DayDock`.
       * Nothing here is hidden from assistive technology, so nothing here needs to refuse a
       * pointer, and the top bar's save is reachable again by pointer and by keyboard alike. */
      /* ── Pressing anything that is not a card and not a control puts the open day away ──────
       * The handoff's panel has no scrim, so the screen behind it is the dismissal. This sits on
       * the whole screen rather than on the week alone, because "the empty space beside the panel"
       * is the empty space anywhere: between the cards, under them, beside the phase chips, along
       * the top bar. Bound only while a day is open, so an ordinary builder ignores every press.
       *
       * FOUR guards, and the first is the one that is easy to get wrong. A React PORTAL bubbles its
       * events through the REACT tree rather than the DOM tree, so every press inside the day panel
       * — which renders into `<body>` — arrives here as a press on the screen behind it, and the
       * panel closed itself the moment a trainer touched anything in it. Containment in this
       * element's own DOM subtree is what tells the two apart; the same trap is recorded on the day
       * card's own handler.
       *
       * Then the three ordinary ones: a press inside a day card, which opens that day; a press on
       * any control, which does its own job; and a press whose target has already left the document,
       * which is what a menu item looks like by the time its click bubbles. */
      ref={pageRef}
      onClick={(event) => {
        if (dock.daySlotToken === null) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.isConnected) return;
        if (pageRef.current?.contains(target) !== true) return;
        if (target.closest('article') !== null) return;
        // The Buddy panel beside the day is the trainer's other surface, not the empty week.
        if (target.closest('[data-assistant-panel]') !== null) return;
        if (target.closest('button, a, input, textarea, select, [role="menuitem"]') !== null) return;
        dock.close();
      }}
    >
      <BuilderTopBar
        planName={heading}
        lifecycle={lifecycle.entries}
        onLifecycleAct={lifecycle.start}
        chips={chrome?.chips ?? []}
        modeChip={chrome?.modeChip ?? null}
        lock={chrome?.lock ?? null}
        save={
          editable
            ? {
                dirty: save.dirty,
                saving: save.saving,
                notice: save.notice,
                blocked: save.blocked,
                offered: save.offersSave,
                canUndo: save.canUndo,
                canRedo: save.canRedo,
                onSave: save.save,
                onUndo: save.undo,
                onRedo: save.redo,
              }
            : null
        }
        assignButtonRef={assignButton}
        showsActs={builder.status !== 'noDraft'}
        views={views}
        assistantButtonRef={assistantButton}
        assistant={
          assistant === null || builder.status !== 'ready'
            ? null
            : {
                open: assistantOpen,
                disabledReason: assistant.entry === 'offered' ? null : t(`web:aiPlan.entry.${assistant.entry}`),
                onToggle: () => {
                  search.update({ asystent: assistantOpen ? undefined : true });
                },
              }
        }
        assign={{
          // A client's running plan is already assigned, and nothing on this screen changes that.
          offered: !isFork,
          canAssign: assign.canAssign,
          disabledReason: assign.disabledReason,
          onOpen: () => {
            setAssigning(true);
          },
        }}
        meta={
          editable && builder.meta !== null && builder.setPlanMeta !== null
            ? {
                view: builder.meta,
                actions: builder.setPlanMeta,
                onRename: setPlanName,
              }
            : null
        }
      />

      {/* Always in the accessibility tree, empty when there is nothing to report — the same rule the
        * overflow banner below follows, and for the same reason: a live region inserted together
        * with its text is not reliably announced. `alert` rather than `status` because a refused
        * save is the trainer's action failing, not a finding about their data. */}
      <p
        className={`${String(styles.saveError)} ${
          save.error == null ? String(styles.saveErrorEmpty) : ''
        }`}
        role="alert"
      >
        {save.error ?? ''}
      </p>

      {/* What the last phase act did. Announced only — the band already SHOWS the outcome, and a
        * visible banner for "Dodano fazę Faza 4" would be a notice about something the trainer can
        * see two rows below. Always mounted and empty until something happens, for the same reason
        * as the two regions above.
        *
        * The band's total is a live region too and it does change ("13 tygodni · 4 fazy"), but it
        * never says WHICH phase appeared — and a copy lands on a tab the trainer was not standing
        * on, so the number moving is the only signal they would get. */}
      <p className={styles.announce} role="status">
        {phase.announcement}
      </p>

      {/* What the plan's own ⋯ did. Announced only, like the phase region above it: a deletion
        * leaves this screen altogether and an ending changes a fact the bar's chips already carry,
        * so a visible banner would be a notice about something that is no longer here or already
        * drawn. Mounted from the first frame and empty until an act lands, for the same reason. */}
      <p className={styles.announce} role="status">
        {lifecycleNotice}
      </p>

      {/* The plan's own irreversible acts. One dialog for all three — only one can be open, the
        * hook holds which, and the prompt it hands over already carries the server's refusal and
        * the in-flight flag. */}
      <ConfirmDialog
        prompt={lifecycle.prompt}
        onConfirm={lifecycle.confirm}
        onCancel={lifecycle.dismiss}
      />

      {/* Base UI unmounts the popup while the dialog is closed, so a half-filled assignment is
        * discarded on close. A plan with no stored row and no client has nothing to assign —
        * `useAssignGate` refuses the button for one — so the dialog is not mounted at all. An
        * unsaved plan built for a client IS assignable, and stays mounted through its confirmation:
        * the settle keeps the store's recipient. `onAssigned` is called for that plan alone — the
        * flow gates it on its entry. */}
      {(resolvedId !== null || recipient !== null) && (
        <AssignPlanDialog
          planId={resolvedId}
          open={assigning}
          finalFocus={assignButton}
          onAssigned={() => {
            // The store settles the tree (it stays on screen, no longer unsaved work) and the device
            // copy ends with it: without the second call the next store change or a pagehide would
            // write the assigned plan back, and „+ Nowy program" would offer a plan the client has.
            settleAssignedPlanDraft();
            settleAssignedNewPlan();
          }}
          onClose={() => {
            setAssigning(false);
            if (assignedTo !== null) {
              void navigate({
                to: '/klienci/$clientId',
                params: { clientId: String(assignedTo) },
                replace: true,
              });
            }
          }}
        />
      )}

      <ConflictDialog
        prompt={save.conflict}
        onAdopt={save.adoptWinning}
        onKeepEditing={save.keepEditing}
      />

      {/* A phase act waiting on an answer — a deletion, or a copy that would move a block the client
        * is already scheduled on. Held by the screen rather than by the band, because a confirm
        * rendered inside the settings popover would be unmounted by the popover closing for it.
        *
        * And the popover DOES close for it, by state — `PhaseEditor` holds its own open flag and
        * drops it when the delete is requested. An earlier note here claimed the popup "unmounts the
        * moment the dialog takes focus"; it does not. A popover is not modal, so nothing about a
        * dialog opening elsewhere dismisses one, and the two stood on screen together with the
        * popover live above the confirm. **Withdrawn.** */}
      <ConfirmDialog
        prompt={phase.prompt}
        onConfirm={phase.confirmAct}
        onCancel={phase.cancelAct}
      />

      {/* Asked BEFORE the tree is sent, unlike the conflict above, which is asked after a save
        * failed. The two never coexist: this one holds the save back, so there is nothing in flight
        * for a 409 to answer. */}
      <ConfirmDialog
        prompt={save.structuralConsent}
        onConfirm={save.confirmStructural}
        onCancel={save.cancelStructural}
      />

      {/* The leave guard. It replaces a native `window.confirm`, which had one sentence for two very
        * different situations and no way to offer the answer a trainer actually wants — see
        * `useSavePlanDocument`. The hook decides the wording, whether the third answer exists and
        * what each button does; this renders it. */}
      <ConfirmDialog
        prompt={save.leave?.prompt ?? null}
        onConfirm={() => save.leave?.onConfirm()}
        onCancel={() => save.leave?.onCancel()}
        onAlternate={
          save.leave?.onAlternate == null
            ? undefined
            : () => {
                save.leave?.onAlternate?.();
              }
        }
      />

      {builder.status === 'ready' && chrome != null ? (
        <>
          <PhaseBar
            phases={chrome.phases}
            total={chrome.bandTotal}
            onSelect={builder.selectPhase}
            panelId={panelId}
            phaseAdd={editable ? chrome.phaseAdd : null}
            add={editable ? phase.add : null}
            phaseEditor={editable ? chrome.phaseEditor : null}
            edit={editable ? phase.edit : null}
            focusSignal={phase.bandFocusSignal}
          />
          {/* The band that says what the canvas below IS: one week run ×3, or a cycle that loops
            * back to its first day. ONE component under both canvases since 13 September 2026 —
            * both mocks draw the same 44px row and differ only in what stands inside its pill. Its
            * ± is a second way into the phase's own length stepper, and it draws none on the
            * sequence canvas, where a phase's cycle count is set in the phase band instead. */}
          {pattern !== null && (
            <PatternBar view={pattern} onResize={editable ? phase.edit.resize : null} />
          )}
          {/* The row stands whether or not it carries a sentence — see `BuilderNote`: mounting one
            * with its text in it moved the canvas 31.3px and announced nothing. It is the LIVE NOTE
            * and nothing else now; the add-day refusal used to be a second row exactly like it and
            * has moved into the column that refuses (owner, 16 September 2026). See `DayColumn`. */}
          <BuilderNote
            message={liveNote.visible ? t('plans.builder.liveNote.body') : ''}
            dismiss={{
              label: t('plans.builder.liveNote.dismissA11y'),
              onDismiss: () => {
                liveNote.dismiss();
                // The ✕ goes with the sentence, so focus would fall to `<body>` and the next Tab
                // would restart at the top of the document. The phase band is the nearest thing
                // that is still standing and already a tab stop — the same answer, by the same
                // means, that a confirmed day deletion gives with the week panel.
                document.getElementById(activeTabId)?.focus();
              },
            }}
          />
          {builder.week != null ? (
            <WeekCanvas
              week={builder.week}
              panelId={panelId}
              labelledBy={activeTabId}
              onRenameSession={builder.renameSession}
              onProgramDay={builder.renameSession === null ? null : dock.open}
              onAddDay={builder.addDay === null ? null : (weekday) => { openNewDay(weekday); }}
              returnFocusToken={dock.returnFocusToken}
              /* The canvas reveals the column the panel opened on — it is the box that scrolls
               * sideways and the panel covers its right-hand 574px. See `useRevealOpenDay`. */
              openDay={
                dock.daySlotToken === null || dock.dock === null
                  ? null
                  : { slotToken: dock.daySlotToken, dock: dock.dock }
              }
              addDayLabel={t('web:planBuilder.addDay')}
              phaseIndex={builder.phaseIndex}
              editable={editable}
              assistantOpen={assistantOpen}
            />
          ) : builder.tape != null ? (
            <TapeCanvas
              tape={builder.tape}
              panelId={panelId}
              labelledBy={activeTabId}
              phaseIndex={builder.phaseIndex}
              /* Same pair as the week's, for the same two jobs — the selection ring, and scrolling
               * the opened column out from under the panel. See `useRevealOpenDay`. */
              openDay={
                dock.daySlotToken === null || dock.dock === null
                  ? null
                  : { slotToken: dock.daySlotToken, dock: dock.dock }
              }
              onRenameSession={builder.renameSession}
              onProgramDay={builder.renameSession === null ? null : dock.open}
              onChangeRest={builder.setRestDaysAfter}
              onAddDay={
                builder.addDay === null
                  ? null
                  : () => {
                      openNewDay(null);
                    }
              }
              returnFocusToken={dock.returnFocusToken}
              editable={editable}
              assistantOpen={assistantOpen}
            />
          ) : null}
          {/* The totals sit UNDER the canvas, above the legend — the canon's own order, and under
            * BOTH canvases since 7 September 2026. The rail judges volume against WEEKLY landmarks,
            * and the tape reaches them by scaling its cycle to seven days with its caption saying
            * so (owner; the KANON sequence sheet's own "normalised to 7 days"). It is drawn
            * UNCONDITIONALLY: a cycle with no day the save would keep states nothing in its cells
            * rather than taking the band off the screen, because the band leaving is the canvas
            * above it growing into the space. See `volumeStatus`'s header. */}
          {/* The mock's rail is the caption block and six cells across the WHOLE width, and an open
            * panel covers its last cells. It used to shrink its grid to keep clear of the panel; the
            * owner reversed that on 8 September 2026 with the rest of the departures. */}
          <BuilderRail volume={chrome.volume} />
          {/* The Buddy panel overlays the week's right edge as the day panel does (owner, 7 October
            * 2026): the week keeps its widths and its own sideways scroll. */}
          {assistantOpen && (
            <BuddyPanel
              buddy={assistant.conversation}
              draftForMessage={assistant.draftForMessage}
              proposalHost={assistant.proposalHost}
              offered={assistant.entry === 'offered'}
              onClose={() => {
                search.update({ asystent: undefined });
                requestAnimationFrame(() => {
                  assistantButton.current?.focus();
                });
              }}
              onHistory={(conversationId) => {
                void navigate({ to: '/buddy', search: { rozmowa: conversationId } });
              }}
              onOpenOnPage={(conversationId, row) => {
                void navigate({
                  to: '/buddy',
                  search: { rozmowa: conversationId, ...(row === undefined ? {} : { szkic: row.turnId, nazwa: row.name }) },
                });
              }}
            />
          )}
          <DayDock
            dock={dock}
            dayTargets={chrome.dayTargets}
            /* What the save has to say, so the open BOX can announce it. Both boxes take the
             * document behind them out of the accessibility tree — the panel through its focus
             * trap, the modal by being modal — so the top bar's own save region and the banner
             * carrying `error` are unreachable for exactly as long as the trainer is programming a
             * day. Both strings therefore come through here.
             *
             * A `notice` wins, as it does in the bar: it reports what just happened, and a save that
             * lands clears the error anyway. */
            saveAnnouncement={save.notice ?? save.error ?? ''}
            besideAssistant={assistantOpen}
          />
        </>
      ) : builder.status === 'loading' ? (
        <BuilderNotice title={t('web:planBuilder.loading')} busy />
      ) : builder.status === 'refused' ? (
        // No retry: a plan this app cannot represent will not become representable on a second
        // read, and a button would tell the trainer it might.
        //
        // One heading, two sentences: the two refusals share the fact ("not here") and differ in
        // the reason, and the reason is the half the trainer needs — one says the plan's DAYS are
        // shapes this editor cannot hold, the other says its whole SYSTEM is.
        <BuilderNotice
          title={t('web:planBuilder.states.refusedTitle')}
          message={
            builder.refusal?.reason === 'unsupportedSchedulingMode'
              ? t('web:planBuilder.states.refusedSchedulingMessage')
              : t('web:planBuilder.states.refusedMessage', {
                  count:
                    builder.refusal?.reason === 'multiSessionDay' ? builder.refusal.dayCount : 1,
                })
          }
          escape={backToLibrary}
        />
      ) : builder.status === 'empty' ? (
        <BuilderNotice
          title={t('web:planBuilder.states.emptyTitle')}
          message={t('web:planBuilder.states.emptyMessage')}
          escape={backToLibrary}
        />
      ) : builder.status === 'noDraft' ? (
        // `/plans/nowy` with nothing in the store: the restore question or the "gone" notice.
        <NewPlanNotice escape={backToLibrary} />
      ) : builder.status === 'notFound' ? (
        // No retry, deliberately. This read is scoped to the caller, so a 404 usually means the
        // plan belongs to another trainer rather than that it is missing — reloading cannot change
        // the answer, and a retry button would promise that it might.
        <BuilderNotice
          title={t('web:planBuilder.states.notFoundTitle')}
          message={t('web:planBuilder.states.notFoundMessage')}
          escape={backToLibrary}
        />
      ) : (
        <BuilderNotice
          title={t('web:planBuilder.states.errorTitle')}
          message={t('web:planBuilder.states.errorMessage')}
          action={{ label: t('web:planBuilder.states.retry'), onClick: builder.retry }}
          escape={backToLibrary}
        />
      )}
    </motion.main>
  );
}
